// src/hooks/useFormFormatting.ts
import { useState, useCallback } from 'react';
import {
  formatName, validateName,
  formatCpf, validateCpf,
  formatWhatsapp, validateWhatsapp,
  formatEmail, validateEmail,
  formatUsername, validateUsername,
} from '../utils/formatUtils';
import { Pessoa } from '../types/usuarios/Pessoa';

// Exporta a interface para uso em outros arquivos
export interface FormattedFields {
  nome: string;
  cpf: string;
  whatsapp: string;
  email: string;
  userName: string;
  codUsuarioIntegracao?: string;
  role?: string; // Adicionado para suportar role
}

// Exporta a interface para uso em outros arquivos
export interface ValidationErrors {
  nome?: string;
  cpf?: string;
  whatsapp?: string;
  email?: string;
  userName?: string;
  codUsuarioIntegracao?: string;
  role?: string; // Adicionado para suportar validação de role
}

export const useFormFormatting = (initialPerson: Pessoa | null) => {
  const [fields, setFields] = useState<FormattedFields>({
    nome: initialPerson?.nome || '',
    cpf: initialPerson?.cpf ? formatCpf(initialPerson.cpf) : '',
    whatsapp: initialPerson?.whatsapp || initialPerson?.contato?.whatsapp || '+55 ',
    email: initialPerson?.email || initialPerson?.contato?.email || '',
    userName: initialPerson?.userName || initialPerson?.usuario?.userName || '',
    codUsuarioIntegracao: initialPerson?.usuario?.codUsuarioIntegracao?.toString(),
    role: initialPerson?.usuario?.role || 'ROLE_USER', // Valor padrão ROLE_USER
  });
  const [errors, setErrors] = useState<ValidationErrors>({});

const handleChange = useCallback((field: keyof FormattedFields, value: string) => {
  let formattedValue = value;
  let error = '';

  switch (field) {
    case 'nome':
      formattedValue = formatName(value);
      if (!validateName(formattedValue)) {
        error = 'Cada palavra deve começar com maiúscula';
      }
      break;
    case 'cpf':
      formattedValue = formatCpf(value);
      if (!validateCpf(formattedValue.replace(/\D/g, ''))) {
        error = 'CPF deve ter 11 dígitos';
      }
      break;
    case 'whatsapp':
      formattedValue = formatWhatsapp(value);
      if (!validateWhatsapp(formattedValue)) {
        error = 'Formato: +55 (99)99999-9999';
      }
      break;
    case 'email':
      formattedValue = formatEmail(value);
      if (!validateEmail(formattedValue)) {
        error = 'Email inválido (ex.: user@domain.com)';
      }
      break;
    case 'userName':
      formattedValue = formatUsername(value);
      if (!validateUsername(formattedValue)) {
        error = 'Apenas letras minúsculas, números, pontos e traços';
      }
      break;
    case 'codUsuarioIntegracao':
      formattedValue = value;
      break;
    case 'role':
      formattedValue = value;
      // Valida role apenas ao editar um usuário que não seja ROLE_MASTER
      if (initialPerson && initialPerson?.usuario?.role !== 'ROLE_MASTER') {
        if (!['ROLE_USER', 'ROLE_ADMIN'].includes(value)) {
          error = 'Role deve ser ROLE_USER ou ROLE_ADMIN';
        }
      }
      break;
  }

  setFields((prev) => ({ ...prev, [field]: formattedValue }));
  setErrors((prev) => ({ ...prev, [field]: error || undefined }));
}, [initialPerson]); // Adiciona initialPerson às dependências

  const handleBlur = useCallback((field: keyof FormattedFields) => {
    if (field === 'email') {
      handleChange('email', fields.email);
    }
  }, [fields.email, handleChange]);

  const isFormValid = (): boolean => {
    return (
      validateName(fields.nome) &&
      validateCpf(fields.cpf.replace(/\D/g, '')) &&
      validateWhatsapp(fields.whatsapp) &&
      validateEmail(fields.email) &&
      validateUsername(fields.userName) &&
      ['ROLE_USER', 'ROLE_ADMIN'].includes(fields.role || 'ROLE_USER') // Valida role
    );
  };

  const getFormattedPerson = (): Pessoa => ({
    nome: fields.nome,
    cpf: fields.cpf.replace(/\D/g, ''),
    contato: {
      whatsapp: fields.whatsapp,
      email: fields.email,
    },
    usuario: {
      userName: fields.userName,
      codUsuarioIntegracao: fields.codUsuarioIntegracao ? Number(fields.codUsuarioIntegracao) : null,
      role: fields.role || 'ROLE_USER', // Inclui role no usuario
    },
  });

  return {
    fields,
    errors,
    handleChange,
    handleBlur,
    isFormValid,
    getFormattedPerson,
    setFields,
  };
};